import React, { useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { useTranslation } from 'react-i18next';

import { uploadDocumentForEmployee } from '../../../api';
import useNotify from '../../../hooks/useNotify';

import styles from './styles.module.scss';

import Input from '../../Core/Input/Input';
import Label from '../../Core/InputLabel';
import { CrossIcon } from '../Icons';

const MAX_FILE_SIZE = 51200 * 1024;
const ALLOWED_FILE_EXTENSION = /\.(pdf|doc|docx|xls|xlsx|png|jpe?g)$/i;

const DocumentUploadFiles = ({
  companyId,
  documentId,
  employee,
  onClose,
  onUploaded,
}) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const [files, setFiles] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const nextUploadFileId = useRef(0);

  const closeForm = () => {
    if (!uploading) onClose();
  };

  const addFiles = (selectedFiles) => {
    const newFiles = Array.from(selectedFiles || []).map((file) => {
      nextUploadFileId.current += 1;
      return { id: nextUploadFileId.current, file };
    });
    if (!newFiles.length) return;
    setFiles((currentFiles) => [...currentFiles, ...newFiles]);
    setErrors((currentErrors) => ({ ...currentErrors, documents: null }));
  };

  const handleFileChange = (event) => {
    const input = event.currentTarget;
    addFiles(input.files);
    input.value = '';
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = {};

    if (!files.length) {
      validationErrors.documents = t('Select at least one file');
    } else if (files.some(({ file }) => file.size > MAX_FILE_SIZE)) {
      validationErrors.documents = t('Each file must be no larger than 50 MB');
    } else if (files.some(({ file }) => !ALLOWED_FILE_EXTENSION.test(file.name))) {
      validationErrors.documents = t('Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG');
    }

    if (effectiveDate && expirationDate && expirationDate < effectiveDate) {
      validationErrors.expiration_date = t('Expiration date must be on or after the effective date');
    }
    if (notes.length > 255) {
      validationErrors.notes = t('Notes cannot exceed 255 characters');
    }
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const formData = new FormData();
    files.forEach(({ file }, index) => {
      formData.append(`documents[${index}][file]`, file);
    });
    if (effectiveDate) formData.append('effective_date', effectiveDate);
    if (expirationDate) formData.append('expiration_date', expirationDate);
    if (notes.trim()) formData.append('notes', notes.trim());

    setUploading(true);
    try {
      const res = await uploadDocumentForEmployee(companyId, employee.id, documentId, formData);
      if (!res) {
        notify.show(t('Unable to upload document'), 'error');
        return;
      }
      notify.show(t('Document uploaded successfully'));
      await onUploaded();
      onClose();
    } catch (error) {
      notify.show(t('Unable to upload document'), 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open
      maxWidth={false}
      onClose={closeForm}
      aria-labelledby='employee-upload-title'
    >
      <div className={styles.header}>
        <div>
          <div id='employee-upload-title' className={styles.title}>
            {t('Upload document')}
          </div>
          <div className={styles.subtitle}>
            {t('Upload a document for {{employee}}', {
              employee: employee.full_name || `${employee.name || ''} ${employee.surname || ''}`.trim(),
            })}
          </div>
        </div>
        <button
          type='button'
          className={styles.closeButton}
          aria-label={t('Close')}
          onClick={closeForm}
          disabled={uploading}
        >
          <CrossIcon />
        </button>
      </div>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.formContent}>
          <div className={styles.formItem}>
            <Label text={t('Documents')} htmlFor='employee-upload-files' />
            <div
              role='button'
              tabIndex={0}
              aria-label={t('Choose files or drop files to upload')}
              className={[
                styles.dropzone,
                dragActive && styles.dropzoneActive,
                errors.documents && styles.dropzoneError,
              ].filter(Boolean).join(' ')}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                addFiles(event.dataTransfer.files);
              }}
            >
              <div className={styles.dropzoneText}>{t('Drop files here or choose files')}</div>
              <div className={styles.hint}>
                {t('PDF, DOC, DOCX, XLS, XLSX, PNG, JPG or JPEG · up to 50 MB per file')}
              </div>
              <span className={styles.chooseFilesButton}>{t('Choose files')}</span>
              <input
                ref={fileInputRef}
                id='employee-upload-files'
                className={styles.hiddenFileInput}
                type='file'
                accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg'
                multiple
                onClick={(event) => event.stopPropagation()}
                onChange={handleFileChange}
              />
            </div>
            {errors.documents ? <div className={styles.error}>{errors.documents}</div> : null}
            {files.length ? (
              <div className={styles.fileList}>
                {files.map(({ id, file }) => (
                  <div key={id} className={styles.file}>
                    <span className={styles.fileName}>{file.name}</span>
                    <button
                      type='button'
                      className={styles.removeFileButton}
                      aria-label={t('Remove {{file}}', { file: file.name })}
                      onClick={() => setFiles((currentFiles) => currentFiles.filter((item) => item.id !== id))}
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className={styles.dateRow}>
            <div className={styles.formItem}>
              <Label text={t('Effective date')} htmlFor='employee-effective-date' />
              <Input
                id='employee-effective-date'
                type='date'
                value={effectiveDate}
                onChange={(event) => {
                  setEffectiveDate(event.target.value);
                  setErrors((currentErrors) => ({
                    ...currentErrors,
                    effective_date: null,
                    expiration_date: null,
                  }));
                }}
                error={errors.effective_date}
                fullWidth
              />
              {errors.effective_date ? <div className={styles.error}>{errors.effective_date}</div> : null}
            </div>
            <div className={styles.formItem}>
              <Label text={t('Expiration date')} htmlFor='employee-expiration-date' />
              <Input
                id='employee-expiration-date'
                type='date'
                value={expirationDate}
                min={effectiveDate || undefined}
                onChange={(event) => {
                  setExpirationDate(event.target.value);
                  setErrors((currentErrors) => ({ ...currentErrors, expiration_date: null }));
                }}
                error={errors.expiration_date}
                fullWidth
              />
              {errors.expiration_date ? <div className={styles.error}>{errors.expiration_date}</div> : null}
            </div>
          </div>
          <div className={styles.formItem}>
            <Label text={t('Notes')} htmlFor='employee-upload-notes' />
            <textarea
              id='employee-upload-notes'
              className={styles.notes}
              maxLength={255}
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
                setErrors((currentErrors) => ({ ...currentErrors, notes: null }));
              }}
            />
            <div className={styles.notesCount}>
              {notes.length}
              /255
            </div>
            {errors.notes ? <div className={styles.error}>{errors.notes}</div> : null}
          </div>
        </div>
        <div className={styles.footer}>
          <button
            type='button'
            className={styles.cancelButton}
            onClick={closeForm}
            disabled={uploading}
          >
            {t('Cancel')}
          </button>
          <button type='submit' className={styles.submitButton} disabled={uploading}>
            {uploading ? t('Uploading...') : t('Upload')}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default DocumentUploadFiles;
